import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Select,
  Tag,
  AutoComplete,
  Input,
  Button,
  Modal,
  Form,
  InputNumber,
  message,
  Upload,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { Api_VariavelGlobal } from '../global';

const Products = () => {
  const navigate = useNavigate();

  // ----------------- States -----------------
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [listaAplicacoes, setListaAplicacoes] = useState([]);

  // Modal visualizar/upload
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);

  // Modal adicionar produto
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [addForm] = Form.useForm();
  const [addConfirmLoading, setAddConfirmLoading] = useState(false);
  const [newProduto, setNewProduto] = useState({ aplicacoes: [] });

  // Modal editar produto
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [editConfirmLoading, setEditConfirmLoading] = useState(false);
  const [aplicacoesSelecionadas, setAplicacoesSelecionadas] = useState([]);

  // ----------------- Fetch inicial -----------------
  useEffect(() => {
    fetchData();
    fetchCategorias();
    fetchAplicacoes();
  }, []);

  const fetchData = async () => {
    try {
      const res = await fetch(`${Api_VariavelGlobal}/api/produtos_todos`);
      const produtos = await res.json();

      const produtosFormatados = produtos.map(produto => ({
        key: produto.ID_PRODUTO,
        id_produto: produto.ID_PRODUTO,
        produto: produto.PRODUTO,
        estoque_atual: produto.ESTOQUE_ATUAL,
        estoque_minimo: produto.ESTOQUE_MINIMO,
        descricao: produto.DESCRICAO,
        preco_custo: produto.PRECO_CUSTO,
        preco_atual: produto.PRECO_ATUAL,
        id_categoria: produto.ID_CATEGORIA,
        sub_categoria: produto.SUB_CATEGORIA,
        status: produto.STATUS,
        imagem: produto.imageUrl,
        aplicacoes: produto.aplicacoes?.map(a => a.ID_APLICACAO) || [],
        aplicacoesNome: produto.aplicacoes?.map(a => a.NOME) || [],
      }));

      setData(produtosFormatados);
      setFilteredData(produtosFormatados);
    } catch (error) {
      console.error('Erro ao buscar os produtos:', error);
      message.error('Erro ao buscar os produtos.');
    }
  };

  const fetchCategorias = async () => {
    try {
      const res = await fetch(`${Api_VariavelGlobal}/api/categorias`);
      const cats = await res.json();
      setCategorias(cats.map(cat => ({ label: cat.NOME, value: cat.ID_CATEGORIA })));
    } catch (error) {
      console.error("Erro ao carregar categorias:", error);
    }
  };

  const fetchAplicacoes = async () => {
    try {
      const res = await fetch(`${Api_VariavelGlobal}/api/aplicacoes`);
      const apps = await res.json();
      setListaAplicacoes(apps); // [{ ID_APLICACAO, NOME }]
    } catch (error) {
      console.error("Erro ao carregar aplicações:", error);
      message.error("Erro ao buscar aplicações.");
    }
  };

  // ----------------- Filtros -----------------
  const handleSearch = (value) => {
    setSearchValue(value);
    const filtered = data.filter(p => p.produto.toLowerCase().includes(value.toLowerCase()));
    setFilteredData(filtered);
    setOptions(filtered.map(p => ({ value: p.produto, label: `Produto: ${p.produto}` })));
  };

  const onSelect = (value) => {
    setSearchValue(value);
    setFilteredData(data.filter(p => p.produto === value));
  };

  const filtrarSemImagem = () => {
    setFilteredData(data.filter(p => !p.imagem || p.imagem.trim() === ''));
  };

  // ----------------- Upload de imagem -----------------
  const showViewModal = (produto) => {
    setSelectedProduct(produto);
    setIsViewModalVisible(true);
    setUploadFile(null);
  };

  const handleViewModalCancel = () => {
    setIsViewModalVisible(false);
    setSelectedProduct(null);
    setUploadFile(null);
  };

  const handleUploadChange = (info) => {
    if (info.fileList.length > 1) info.fileList = info.fileList.slice(-1);
    const file = info.fileList[0]?.originFileObj;
    setUploadFile(file);
  };

  const handleUpload = async () => {
    if (!uploadFile) { message.error('Selecione um arquivo'); return; }
    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('imagem', uploadFile);
      const res = await fetch(`${Api_VariavelGlobal}/api/upload_imagem/${selectedProduct.id_produto}`, { method: 'POST', body: formData });
      if (res.ok) {
        message.success('Imagem enviada com sucesso!');
        fetchData();
        handleViewModalCancel();
      } else message.error('Falha ao enviar imagem.');
    } catch (error) {
      console.error(error);
      message.error('Erro ao enviar imagem.');
    } finally { setUploadLoading(false); }
  };
  
  // ----------------- Encontrar Aplicação Produto -----------------
  
  const fetchAplicacoesProduto = async (idProduto) => {
      try {
        const res = await fetch(
          `${Api_VariavelGlobal}/api/produtos/${idProduto}/aplicacoes`
        );
        const apps = await res.json();
    
        const ids = apps.map(app => app.ID_APLICACAO);
    
        setAplicacoesSelecionadas(ids);
        editForm.setFieldsValue({ aplicacoes: ids });
    
      } catch (error) {
        console.error('Erro ao buscar aplicações do produto:', error);
        message.error('Erro ao carregar aplicações do produto');
      }
    };

  // ----------------- Adicionar Produto -----------------
  const showAddModal = () => setIsAddModalVisible(true);
  const handleAddModalCancel = () => {
    setIsAddModalVisible(false);
    addForm.resetFields();
    setNewProduto({ aplicacoes: [] });
  };

  const handleAddSave = async () => {
    try {
      const values = await addForm.validateFields();
      setAddConfirmLoading(true);
      const res = await fetch(`${Api_VariavelGlobal}/api/produtos_add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...values, aplicacoes: newProduto.aplicacoes || [] }),
      });
      if (res.ok) {
        message.success('Produto criado com sucesso!');
        fetchData();
        handleAddModalCancel();
      } else message.error('Erro ao criar produto.');
    } catch (error) {
      console.error(error);
      message.error('Erro ao criar produto.');
    } finally { setAddConfirmLoading(false); }
  };

  // ----------------- Editar Produto -----------------
      const showEditModal = (produto) => {
      setSelectedProduct(produto);
      setIsEditModalVisible(true);
    
      editForm.setFieldsValue({
        produto: produto.produto,
        estoque_atual: produto.estoque_atual,
        estoque_minimo: produto.estoque_minimo || 0,
        descricao: produto.descricao || '',
        preco_custo: produto.preco_custo,
        preco_atual: produto.preco_atual,
        id_categoria: produto.id_categoria,
        sub_categoria: produto.sub_categoria || '',
        status: produto.status,
      });
    
      // 🔥 busca real no backend
      fetchAplicacoesProduto(produto.id_produto);
    };

  const handleEditModalCancel = () => {
    setIsEditModalVisible(false);
    setSelectedProduct(null);
    setAplicacoesSelecionadas([]);
    editForm.resetFields();
  };

  const handleEditSave = async () => {
    try {
      const values = await editForm.validateFields();
      setEditConfirmLoading(true);

      const res = await fetch(`${Api_VariavelGlobal}/api/produtos_update/${selectedProduct.id_produto}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });

      if (!res.ok) return message.error('Erro ao atualizar produto.');

      // Atualiza aplicações
      await fetch(`${Api_VariavelGlobal}/api/produtos/${selectedProduct.id_produto}/aplicacoes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ aplicacoes: aplicacoesSelecionadas }),
      });

      message.success('Produto atualizado com sucesso!');
      fetchData();
      handleEditModalCancel();
    } catch (error) {
      console.error(error);
      message.error('Erro ao atualizar produto.');
    } finally { setEditConfirmLoading(false); }
  };

  // ----------------- Atualizar Estoque -----------------
  const atualizarEstoque = async (produto, novoEstoque) => {
    try {
      const res = await fetch(`${Api_VariavelGlobal}/api/produtos_update/${produto.id_produto}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...produto, estoque_atual: novoEstoque }),
      });
      if (!res.ok) { message.error('Erro ao atualizar estoque!'); return; }
      setData(prev => prev.map(p => p.id_produto === produto.id_produto ? { ...p, estoque_atual: novoEstoque } : p));
      setFilteredData(prev => prev.map(p => p.id_produto === produto.id_produto ? { ...p, estoque_atual: novoEstoque } : p));
      message.success('Estoque atualizado!');
    } catch (error) {
      console.error(error);
      message.error('Erro ao atualizar estoque.');
    }
  };

  // ----------------- Colunas da Tabela -----------------
  const columns = [
    {
      title: 'Imagem',
      dataIndex: 'imagem',
      key: 'imagem',
      render: (imagem, record) => (
        <Button type="link" onClick={() => showViewModal(record)} style={{ padding: 0, border: 'none' }}>
          {imagem ? (
            <img src={`https://gestor.bebilogo.com.br/api/uploads/${imagem}`} alt={record.produto} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }} />
          ) : (
            <div style={{ width: 50, height: 50, backgroundColor: '#f0f0f0', borderRadius: 4 }} />
          )}
        </Button>
      )
    },
    { title: 'Produto', dataIndex: 'produto', key: 'produto', render: (text, record) => <Button type="link" onClick={() => showViewModal(record)}>{text}</Button> },
    { title: 'Estoque Atual', dataIndex: 'estoque_atual', key: 'estoque_atual', render: (estoque, record) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Button type="primary" danger size="small" onClick={() => atualizarEstoque(record, record.estoque_atual - 1)} disabled={record.estoque_atual <= 0}>-</Button>
        <strong style={{ minWidth: 30, textAlign: 'center' }}>{estoque}</strong>
        <Button type="primary" size="small" onClick={() => atualizarEstoque(record, record.estoque_atual + 1)}>+</Button>
      </div>
    )},
    { title: 'Categoria', dataIndex: 'id_categoria', key: 'id_categoria' },
  {
  title: 'Aplicações',
  dataIndex: 'aplicacoesNome',
  key: 'aplicacoes',
  render: (aplicacoes) =>
    aplicacoes?.length
      ? aplicacoes.map(nome => (
          <Tag color="blue" key={nome}>{nome}</Tag>
        ))
      : <Tag color="default">Nenhuma</Tag>
},

    { title: 'Preço Custo', dataIndex: 'preco_custo', key: 'preco_custo', render: preco => `R$ ${Number(preco || 0).toFixed(2).replace('.', ',')}` },
    { title: 'Preço Venda', dataIndex: 'preco_atual', key: 'preco_atual', render: preco => `R$ ${Number(preco || 0).toFixed(2).replace('.', ',')}` },
    { title: 'Margem de Lucro', key: 'margem_lucro', render: (_, record) => {
      const custo = Number(record.preco_custo); const venda = Number(record.preco_atual);
      if (isNaN(custo) || isNaN(venda) || custo <= 0) return '0%';
      return `${((venda - custo) / custo * 100).toFixed(2).replace('.', ',')}%`;
    }},
    { title: 'Status', dataIndex: 'status', key: 'status', render: status => <Tag color={status === 1 ? 'blue' : 'red'}>{status === 1 ? 'Ativo' : 'Inativo'}</Tag> },
    { title: 'Ação', key: 'acao', render: (_, record) => <Button type="link" onClick={() => showEditModal(record)}>Editar</Button> },
  ];

  // ----------------- Render -----------------
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 12 }}>
        <AutoComplete popupMatchSelectWidth={252} style={{ width: 300 }} value={searchValue} options={options} onSelect={onSelect} onSearch={handleSearch} size="large">
          <Input.Search size="large" placeholder="Buscar por Produto" enterButton />
        </AutoComplete>

        <Select placeholder="Mais opções" size="large" style={{ width: 220 }} onChange={value => {
          if (value === 'sem_imagem') filtrarSemImagem();
          if (value === 'todos') setFilteredData(data);
        }} options={[
          { label: 'Produtos Sem Imagem', value: 'sem_imagem' },
          { label: 'Todos os Produtos', value: 'todos' },
        ]} />

        <Button style={{ width: 200 }} size="large" type="default" onClick={() => navigate('/estoque')}>Estoque</Button>
        <Button style={{ width: 200 }} size="large" type="default" onClick={() => navigate('/sugestao')}>Sugesto de Compra</Button>
        <Button style={{ width: 200 }} size="large" type="primary" onClick={showAddModal}>Novo Produto</Button>
      </div>

      <Table columns={columns} dataSource={filteredData} pagination={{ pageSize: 10 }} rowClassName="cursor-pointer" size="small" />

      {/* Modal Adicionar Produto */}
      <Modal title="Adicionar Produto" open={isAddModalVisible} onCancel={handleAddModalCancel} onOk={handleAddSave} okText="Adicionar" cancelText="Cancelar" confirmLoading={addConfirmLoading}>
        <Form form={addForm} layout="vertical" initialValues={{ preco_custo: 0, sub_categoria: '', estoque_atual: 0, estoque_minimo: 0 }}>
          <Form.Item name="produto" label="Produto" rules={[{ required: true, message: 'Digite o nome do produto' }]}><Input /></Form.Item>
          <Form.Item name="descricao" label="Descrição"><Input /></Form.Item>
          <Form.Item name="id_categoria" label="Categoria" rules={[{ required: true, message: 'Selecione a categoria' }]}><Select placeholder="Selecione a categoria" options={categorias} /></Form.Item>
          {aplicacoesSelecionadas.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <strong>Aplicações atuais do produto:</strong>
                <div style={{ marginTop: 6 }}>
                  {listaAplicacoes
                    .filter(app => aplicacoesSelecionadas.includes(app.ID_APLICACAO))
                    .map(app => (
                      <Tag color="geekblue" key={app.ID_APLICACAO}>
                        {app.NOME}
                      </Tag>
                    ))}
                </div>
              </div>
            )}
          <Form.Item label="Aplicações">
            <Select mode="multiple" placeholder="Selecione as aplicações" value={newProduto.aplicacoes} onChange={(values) => setNewProduto(prev => ({ ...prev, aplicacoes: values }))}>
              {listaAplicacoes.map(app => <Select.Option key={app.ID_APLICACAO} value={app.ID_APLICACAO}>{app.NOME}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="estoque_atual" label="Estoque Atual" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
          <Form.Item name="estoque_minimo" label="Estoque Mínimo" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
          <Form.Item name="sub_categoria" label="Subcategoria"><Input hidden /></Form.Item>
          <Form.Item name="preco_custo" label="Preço Custo"><InputNumber hidden style={{ width: '100%' }} min={0} step={0.01} /></Form.Item>
          <Form.Item name="preco_atual" label="Preço Venda" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={0} step={0.01} /></Form.Item>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={0} max={1} /></Form.Item>
        </Form>
      </Modal>

      {/* Modal Editar Produto */}
      <Modal title="Editar Produto" open={isEditModalVisible} onCancel={handleEditModalCancel} onOk={handleEditSave} okText="Salvar" cancelText="Cancelar" confirmLoading={editConfirmLoading}>
        <Form form={editForm} layout="vertical">
          <Form.Item name="produto" label="Produto" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="estoque_atual" label="Estoque Atual" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
          <Form.Item name="estoque_minimo" label="Estoque Mínimo" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={0} /></Form.Item>
          <Form.Item name="descricao" label="Descrição"><Input /></Form.Item>
          <Form.Item name="preco_custo" label="Preço Custo"><InputNumber style={{ width: '100%' }} min={0} step={0.01} /></Form.Item>
          <Form.Item name="preco_atual" label="Preço Venda"><InputNumber style={{ width: '100%' }} min={0} step={0.01} /></Form.Item>
          <Form.Item name="id_categoria" label="Categoria"><Select placeholder="Selecione a categoria" options={categorias} /></Form.Item>
          <Form.Item name="sub_categoria" label="Subcategoria"><Input /></Form.Item>
          <Form.Item name="status" label="Status"><InputNumber style={{ width: '100%' }} min={0} max={1} /></Form.Item>
            <Form.Item label="Aplicações" name="aplicacoes">
              <Select
                mode="multiple"
                placeholder="Selecione as aplicações"
                value={aplicacoesSelecionadas}
                onChange={setAplicacoesSelecionadas}
                allowClear
              >
                {listaAplicacoes.map(app => (
                  <Select.Option
                    key={app.ID_APLICACAO}
                    value={app.ID_APLICACAO}
                  >
                    {app.NOME}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
        </Form>
      </Modal>

      {/* Modal Visualizar/Upload Imagem */}
      <Modal title={`Imagem do Produto: ${selectedProduct?.produto}`} open={isViewModalVisible} onCancel={handleViewModalCancel} onOk={handleUpload} okText="Enviar" cancelText="Cancelar" confirmLoading={uploadLoading}>
        <Upload beforeUpload={() => false} maxCount={1} onChange={handleUploadChange}><Button icon={<UploadOutlined />}>Selecionar Imagem</Button></Upload>
        {selectedProduct?.imagem && <img src={`https://gestor.bebilogo.com.br/api/uploads/${selectedProduct.imagem}`} alt={selectedProduct.produto} style={{ marginTop: 16, width: '100%' }} />}
      </Modal>
    </>
  );
};

export default Products;
